// frontend/src/features/sales-rep/components/RevenueTreemap.tsx
import React, { useMemo } from 'react';
import {
    Treemap,
    ResponsiveContainer,
    Tooltip
} from 'recharts';
import { SalesRepData } from '../services/salesRepAPI';

interface RevenueTreemapProps {
    data: SalesRepData[];
}

// Cores
const COLORS = [
  '#F03A3D', '#E22326', '#DD1518', '#C11215', '#B01214', 
  '#6C7BA6', '#3E4D73', '#1B2A49'
];

const RevenueTreemap: React.FC<RevenueTreemapProps> = ({ data }) => {
    // 1. Calcular o Total Geral para a porcentagem
    const totalRevenue = useMemo(() => {
        return data.reduce((acc, curr) => acc + Number(curr.totalVenda), 0);
    }, [data]);

    const treeData = data
        .map((item) => ({
            name: item.nomeVendedor,
            size: Number(item.totalVenda),
            originalData: item
        }))
        .sort((a, b) => b.size - a.size);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            notation: 'compact',
            compactDisplay: 'short'
        }).format(value);
    };

    // Renderizador Customizado do Bloco
    const CustomizedContent = (props: any) => {
        const { depth, x, y, width, height, index, name, size } = props;

        // Só renderiza se houver espaço suficiente
        if (width < 60 || height < 50) return null;

        // Cálculo da Porcentagem (Mesma lógica do Tooltip)
        const percent = totalRevenue > 0 
            ? ((size / totalRevenue) * 100).toFixed(1) + '%' 
            : '0%';

        return (
            <g>
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    style={{
                        fill: COLORS[index % COLORS.length],
                        stroke: '#fff',
                        strokeWidth: 2 / (depth + 1e-10),
                        strokeOpacity: 1,
                    }}
                />
                {/* 1. Nome (Topo) */}
                <text
                    x={x + width / 2}
                    y={y + height / 2 - 9} // Subi um pouco
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={11}
                    fontWeight="200"
                    style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.4)' }}
                >
                    {name.split(' ')[0]}
                </text>
                
                {/* 2. Valor Monetário (Meio) 
                <text
                    x={x + width / 2}
                    y={y + height / 2 + 4}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={12}
                    fontWeight="200"
                    style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.4)' }}
                >
                    {formatCurrency(size)}
                </text>
                */}
                {/* 3. Porcentagem (Base) - ADICIONADO */}
                <text
                    x={x + width / 2}
                    y={y + height / 2 + 10} // Logo abaixo do valor
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={13}
                    fontWeight="200"
                    
                >
                    ({percent})
                </text>
            </g>
        );
    };

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const item = payload[0].payload;
            const percent = ((item.size / totalRevenue) * 100).toFixed(1);

            return (
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    padding: '12px',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-base, 8px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                        {item.name}
                    </p>
                    <p style={{ margin: 0, color: '#0088FE' }}>
                        💰 Faturamento: {formatCurrency(item.size)}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.9em', color: '#666' }}>
                        📊 Representatividade: <b>{percent}%</b>
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            <Treemap
                data={treeData}
                dataKey="size"
                aspectRatio={4 / 3}
                stroke="#fff"
                fill="#8884d8"
                content={<CustomizedContent />}
            >
                <Tooltip content={<CustomTooltip />} />
            </Treemap>
        </ResponsiveContainer>
    );
};

export default RevenueTreemap;